import { CheckIcon } from "@radix-ui/react-icons";
import { useCallback, useMemo, useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DataTableFacetedFilterProps, OnSelectFn } from "./@types";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "../ui/button";

export function DataTableFacetedFilter<TData>({
  columnId,
  options,
  table,
  title,
  selectOnce = false,
  onSelect: onSelectCb,
}: DataTableFacetedFilterProps<TData>) {
  const column = useMemo(() => {
    return table.getColumn(columnId);
  }, [columnId, table]);

  const facets = useMemo(() => {
    if (!column) return null;
    return column.getFacetedUniqueValues();
  }, [column]);

  const [commandListOpen, setCommandListOpen] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState(
    new Set(column?.getFilterValue() as string[])
  );

  const toggleCommandListOpenChange = useCallback(() => {
    setCommandListOpen(!commandListOpen);
  }, [commandListOpen]);

  const onSelect = useCallback<OnSelectFn>(
    ({ isSelected, value }) => {
      const newSelectedValues = new Set(selectedValues);
      if (isSelected) newSelectedValues.delete(value);
      else if (!value) newSelectedValues.clear();
      else if (selectOnce) {
        newSelectedValues.clear();
        newSelectedValues.add(value);
      } else newSelectedValues.add(value);

      const filterValues = Array.from(newSelectedValues);
      column?.setFilterValue(filterValues.length ? filterValues : undefined);

      if (onSelectCb) {
        if (filterValues.length <= 0)
          onSelectCb({ isSelected: false, value: "", label: "" });
        else
          onSelectCb({
            isSelected: true,
            value: filterValues[0],
            label: filterValues[0],
          });
      }

      setSelectedValues(newSelectedValues);
      if (selectOnce) toggleCommandListOpenChange();
    },
    [
      column,
      onSelectCb,
      selectOnce,
      selectedValues,
      toggleCommandListOpenChange,
    ]
  );

  const clearSelection = useCallback(() => {
    onSelect({ isSelected: false, label: "", value: "" });
  }, [onSelect]);

  return (
    <Popover open={commandListOpen} onOpenChange={toggleCommandListOpenChange}>
      <PopoverTrigger asChild disabled={options.length === 0}>
        <Button
          variant="outline"
          className={cn(
            "bg-[#37393F] border-0  hover:shadow-xl hover:ring-0",
            selectedValues?.size > 0 && "border border-primary text-primary"
          )}
        >
          {selectedValues.size > 0
            ? Array.from(selectedValues).join(", ")
            : title}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="bg-textWhite max-w-[216px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList className="w-full">
            <CommandEmpty>Nenhum item encontrado.</CommandEmpty>
            {selectedValues?.size > 0 && !selectOnce && (
              <Button
                variant="link"
                className="w-full text-white"
                onClick={clearSelection}
              >
                Limpar selecção
              </Button>
            )}
            <CommandGroup>
              {options.map(({ label, value }) => {
                const isSelected = selectedValues.has(value);
                return (
                  <CommandItem
                    key={value}
                    disabled={false}
                    onSelect={() => onSelect({ isSelected, label, value })}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        isSelected
                          ? "bg-primary text-background border-primary"
                          : "opacity-50 [&_svg]:invisible"
                      )}
                    >
                      <CheckIcon className={cn("h-4 w-4")} />
                    </div>

                    <span>{label}</span>
                    {facets?.get(String(value)) !== undefined && (
                      <span className="text-white ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                        {String(facets.get(String(value)))}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
